import React, { useEffect, useMemo, useState } from "react";
import {
  BIG_FIVE_FACETS_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_BIG_FIVE,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";

import { getBigFiveTraitResultCode } from "../../utils/assessment-utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/loader";
import Header from "../../components/header";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import { getBigFivePersonalityBannerImage } from "../../utils/utility-functions";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";

import { useTranslation } from "react-i18next";
import { CallToActionSection } from "../../components/call-to-action/component";
import InviteFriend from "../../components/invite-friend";
import PathHelper from "../../utils/path-helper";
import ReportWrapper from "../../components/report-wrapper";

// ------------- Basis of Trait code filter the content -------------

const filterContentByTraitDetails = (content, traitDetails) => {
  if (!content || !traitDetails) return [];
  return Object.entries(content)
    .filter(([key]) => traitDetails.includes(key))
    .map(([key, value]) => ({ key, value }));
};

// --------------- Extract Trait Code Like This "C_A" ," A_A" --------------

const getTraitDetailsFromUserTraits = (userTraits) => {
  return userTraits.map((item) => {
    const { score, trait } = item;
    const { title } = trait; // Extracting title from the nested trait object
    return getBigFiveTraitResultCode(score, title);
  });
};

// -------------- Add fullName in UserTraits Array (Extract fullName from filtered content data ) ------------------
const findMatch = (environmentData, traitData) => {
  return environmentData
    .map((item) => {
      const keyWithoutSuffix = item.key.split("_")[0]; // Remove suffix
      const matchedTrait = traitData.find(
        (trait) => trait.trait.title === keyWithoutSuffix
      );
      return matchedTrait
        ? {
            matchedTrait: {
              ...matchedTrait,
              fullName: item.value.FullName, // Add FullName to matchedTrait
            },
          }
        : null;
    })
    .filter(Boolean); // Remove null values
};

function BigFiveReport({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  funnelStep,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,
  getGuestUserConnect,
  getTransactionDetailConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) {
  const { t } = useTranslation();
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const [userData, setUserData] = useState(null);
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  const assessmentId = currentAssessmentAttemptData?.assessmentId;

  const userId = queryParams.get("uid") || userGUID;

  const [isLoading, setIsLoading] = useState(false);

  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];
  const traitColors = {
    O: "blue",
    C: "green",
    E: "yellow",
    A: "red",
    N: "purple",
  };

  const navigate = useNavigate();
  // const token = getCookie("jwtToken");

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_BIG_FIVE[currentLocale] ||
            MODEL_TYPE_BIG_FIVE[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  const matchedData = useMemo(() => {
    const traitDetails = getTraitDetailsFromUserTraits(userTraits);
    return filterContentByTraitDetails(contentData, traitDetails);
  }, [userTraits, contentData]);

  const manupulateUserTraits = findMatch(matchedData, userTraits);

  if (isLoading) {
    return (
      <div>
        {" "}
        <Loader isLoading={isLoading} />
      </div>
    );
  }
  const createSectionHeader = (title, gradientColors, imageSrc) => {
    return (
      <div
        className={`relative h-64 sm:h-96 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-700`}
      >
        <img
          src={imageSrc}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-auto md:h-full object-cover object-right"
        />
        <div
          className={`absolute inset-0 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-700- to-transparent`}
        ></div>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute inset-0">
          <svg className="absolute h-full w-full" preserveAspectRatio="none">
            <pattern
              id="dots"
              x="0"
              y="0"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="2" cy="2" r="1" fill="rgba(255,255,255,0.1)" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>
        <div
          className="absolute top-1/2 left-8 transform -translate-y-1/2 text-white z-10"
          id={reportType}
        >
          <h2
            className="text-3xl sm:text-7xl font-bold mb-6 text-white shadow-text"
            id={reportType}
          >
            {title}
          </h2>
        </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="container mx-auto p-4 sm:p-8">
        <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          {/* Hero Banner */}
          <div className="relative h-64 sm:h-96 bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600">
            <img
              src={getBigFivePersonalityBannerImage("looking-ahead")}
              alt="Your Big 5 Personality Profile"
              className="absolute bottom-0 right-0 w-full md:w-2/3 h-auto md:h-full object-cover object-right"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600-to-transparent"></div>
            <div className="absolute inset-0 bg-black opacity-10"></div>
            <div className="absolute inset-0">
              <svg
                className="absolute h-full w-full"
                preserveAspectRatio="none"
              >
                <pattern
                  id="dots"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <circle cx="2" cy="2" r="1" fill="rgba(255,255,255,0.1)" />
                </pattern>
                <rect width="100%" height="100%" fill="url(#dots)" />
              </svg>
            </div>
            <div className="relative h-full flex items-center" id={reportType}>
              <div className="w-full px-8 py-12" id={reportType}>
                <h2
                  className="text-4xl sm:text-6xl font-bold mb-6 text-white shadow-text "
                  id={reportType}
                >
                  {t("report.bigfive.title")}
                </h2>
              </div>
            </div>
          </div>
          {isAuth && (
            <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-4">
              <ShareButton
                currentAssessmentAttemptData={currentAssessmentAttemptData}
                userGUID={userId}
                userData={userData}
              />
              <SendToFriendButton
                currentAssessmentAttemptData={currentAssessmentAttemptData}
                userGUID={userGUID}
                userData={userData}
                CreateGuestUserConnect={sendToFriendReportOnEmailConnect}
              />
            </div>
          )}
          {/* Content Section */}
          <div className="p-8 sm:p-12">
            {/* Introduction Text */}

            <div className="max-w-auto mx-auto mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">
                {" "}
                {contentData && contentData?.Intro?.RootHeading}
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                {contentData && contentData?.Intro?.Content}
              </p>
            </div>

            {/* Trait Visualization */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Trait Summary */}
              <div className="space-y-4">
                {manupulateUserTraits?.map((trait) => {
                  const traitTitle = trait?.matchedTrait?.trait?.title || "";
                  const colorClass = traitColors[traitTitle] || "gray";
                  return (
                    <div
                      key={trait.matchedTrait.trait.id}
                      className={`bg-${colorClass}-50 rounded-lg p-4`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-gray-700">
                          {trait.matchedTrait.fullName}
                        </span>
                        <span className={`text-sm text-${colorClass}-600`}>
                          {trait.matchedTrait.percentage}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`bg-${colorClass}-500 h-2 rounded-full transition-all duration-500`}
                          style={{ width: `${trait.matchedTrait.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Key Insights */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  {t("report.bigfive.key_insights")}
                </h3>
                <ul className="space-y-4">
                  {matchedData.map((item) => {
                    const traitTitle = item?.key.split("_")[0];
                    const colorClass = traitColors[traitTitle] || "gray";
                    return (
                      <li key={item.id} className="flex items-start">
                        <span
                          className={`flex-shrink-0 w-2 h-2 rounded-full bg-${colorClass}-500  mt-2 mr-2`}
                        ></span>
                        <p className="text-gray-600">
                          {item.value.Overview.split(".")[0] + "."}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-purple-50 rounded-xl p-6 text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">5</div>
                <div className="text-gray-600">
                  {t("report.bigfive.core_personality_dimensions")}
                </div>
              </div>
              <div className="bg-indigo-50 rounded-xl p-6 text-center">
                <div className="text-4xl font-bold text-indigo-600 mb-2">
                  30+
                </div>
                <div className="text-gray-600">
                  {t("report.bigfive.personality_facets")}
                </div>
              </div>
              <div className="bg-blue-50 rounded-xl p-6 text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  100%
                </div>
                <div className="text-gray-600">
                  {t("report.bigfive.personalized_profile")}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          {createSectionHeader(
            contentData?.OverviewHeading,
            "from-blue-500 via-blue-600 to-indigo-700",
            getBigFivePersonalityBannerImage("big_five_overview")
          )}
          {/* Create section header */}
          <div className="p-6 sm:p-10">
            <div className="space-y-6">
              {" "}
              {matchedData?.map((item, index) => {
                const traitTitle = item?.key.split("_")[0];
                const colorClass = traitColors[traitTitle] || "gray";
                return (
                  <div
                    key={index}
                    className={`bg-${colorClass}-50 p-6 rounded-lg`}
                  >
                    <h3 className="text-2xl font-bold mb-4 text-indigo-800">
                      {item?.value?.FullName}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {item?.value?.Overview}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="relative h-64 sm:h-96 bg-gradient-to-r from-green-600 via-green-500 to-green-600">
            <img
              src={getBigFivePersonalityBannerImage("environmental_traits")}
              // src="https://insightfultraits.com/wp-content/uploads/2024/08/looking-ahead.png"
              alt="Your Big 5 Personality Profile"
              className="absolute bottom-0 right-0 w-full md:w-2/3 h-auto md:h-full object-cover object-right"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-green-600 via-green-500 to-indigo-600-to-transparent"></div>
            <div className="absolute inset-0 bg-black opacity-10"></div>
            <div className="absolute inset-0">
              <svg
                className="absolute h-full w-full"
                preserveAspectRatio="none"
              >
                <pattern
                  id="dots"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <circle cx="2" cy="2" r="1" fill="rgba(255,255,255,0.1)" />
                </pattern>
                <rect width="100%" height="100%" fill="url(#dots)" />
              </svg>
            </div>
            <div className="relative h-full flex items-center">
              <div className="w-full px-8 py-12">
                <h2 className="text-4xl sm:text-7xl font-bold mb-6 text-white shadow-text">
                  {contentData.EnvironmentsHeading}
                </h2>
                <p className="text-xl sm:text-2xl text-white shadow-text max-w-3xl"></p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-10">
            <p className="text-lg text-gray-700 mb-8">
              {contentData?.EnvironmentFlavorText}
            </p>

            {/* -------------------- Social Environment --------------------- */}

            <div className="mb-12">
              <div className="flex justify-start items-center text-purple-600  w-full gap-2">
                <div id="icon2" className="w-16 h-16">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold ">
                  {contentData.SocialHeading}
                </h3>
              </div>

              {/* Map over FAQ content */}
              {matchedData?.map((item, index) => {
                const traitTitle = item?.key.split("_")[0];
                const colorClass = traitColors[traitTitle] || "gray";
                return (
                  <div
                    key={index}
                    className={`bg-${colorClass}-50 p-6 rounded-lg mb-4`}
                  >
                    <h3 className="text-xl font-bold mb-2">
                      {item?.value?.FullName}
                    </h3>
                    <p className="text-gray-700">
                      {item?.value?.Environments?.[0]?.Social && (
                        <p className="text-gray-600">
                          {item.value.Environments[0].Social}
                        </p>
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
            {/* -------------------------- Work Environment ------------------- */}

            <div className="mb-12">
              <div className="flex justify-start items-center text-purple-600  w-full gap-2">
                <div id="icon2" className="w-16 h-16 ">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
                    />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold">
                  {contentData.WorkHeading}
                </h3>
              </div>

              {matchedData?.map((item, index) => {
                const traitTitle = item?.key.split("_")[0];
                const colorClass = traitColors[traitTitle] || "gray";
                return (
                  <div
                    key={index}
                    className={`bg-${colorClass}-50 p-6 rounded-lg mb-4`}
                  >
                    <h3 className="text-xl font-bold mb-2">
                      {item?.value?.FullName}
                    </h3>
                    <p className="text-gray-700">
                      {item?.value?.Environments?.[0]?.Work && (
                        <p className="text-gray-600">
                          {item.value.Environments[0].Work}
                        </p>
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
            {/* -------------------------- Family Environment ------------------- */}
            <div className="mb-12">
              <div className="flex justify-start items-center text-purple-600  w-full gap-2">
                <div id="icon2" className="w-16 h-16 ">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold ">
                  {contentData.FamilyHeading}
                </h3>
              </div>

              {matchedData?.map((item, index) => {
                const traitTitle = item?.key.split("_")[0];
                const colorClass = traitColors[traitTitle] || "gray";
                return (
                  <div
                    key={index}
                    className={`bg-${colorClass}-50 p-6 rounded-lg mb-4`}
                  >
                    <h3 className="text-xl font-bold mb-2">
                      {item?.value?.FullName}
                    </h3>
                    <p className="text-gray-700">
                      {item?.value?.Environments?.[0]?.Family && (
                        <p className="text-gray-600">
                          {item.value.Environments[0].Family}
                        </p>
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
            {/* -------------------------- Romantic Environment ------------------- */}

            <div className="mb-12">
              <div className="flex justify-start items-center text-purple-600  w-full gap-2">
                <div id="icon2" className="w-16 h-16 ">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold ">
                  {contentData.RomanticHeading}
                </h3>
              </div>

              {matchedData?.map((item, index) => {
                const traitTitle = item?.key.split("_")[0];
                const colorClass = traitColors[traitTitle] || "gray";
                return (
                  <div
                    key={index}
                    className={`bg-${colorClass}-50 p-6 rounded-lg mb-4`}
                  >
                    <h3 className="text-xl font-bold mb-2">
                      {item?.value?.FullName}
                    </h3>
                    <p className=" text-gray-700 ">
                      {item?.value?.Environments?.[0]?.Romantic && (
                        <p className="text-gray-600">
                          {item.value.Environments[0].Romantic}
                        </p>
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        {isAuth ? (
          <CallToActionSection
            currentLocale={currentLocale}
            t={t}
            getAssessmentConnect={getAssessmentConnect}
            getAssessmentAttemptByUserIdConnect={
              getAssessmentAttemptByUserIdConnect
            }
            assessmentId={assessmentId}
            userId={userGUID}
            bgColor={ASSESSMENT_BACKGROUND_COLORS.BLUE_INDIGO}
          />
        ) : (
          <section className="space-y-4">
            <div className="bg-indigo-600 text-white rounded-xl p-6 text-center shadow-lg">
              <p className="text-xl font-bold mb-2">{contentData?.CTAFacets}</p>
              <button
                className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors"
                onClick={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${BIG_FIVE_FACETS_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
              >
                {t("dashboard.take_test_cta")}
              </button>
            </div>

            <div className="bg-purple-600 text-white rounded-xl p-6 text-center shadow-lg">
              <p className="text-xl font-bold mb-2">
                {contentData?.CTAPersonalityTest}
              </p>
              <button
                className="bg-white text-purple-600 px-6 py-2 rounded-full font-semibold hover:bg-purple-100 transition-colors"
                onClick={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
              >
                {t("dashboard.discover_your_test")}
              </button>
            </div>

            <div className="bg-blue-600 text-white hidden rounded-xl p-6 text-center shadow-lg">
              <p className="text-xl font-bold mb-2">
                {contentData?.CTAAITools}
              </p>
              <button className="bg-white text-blue-600 px-6 py-2 rounded-full font-semibold hover:bg-blue-100 transition-colors">
                {t("dashboard.explore_ai_tools")}
              </button>
            </div>
          </section>
        )}

        {isAuth && (
          <InviteFriend
            sectionMode={true}
            getGuestUserConnect={getGuestUserConnect}
            getTransactionDetailConnect={getTransactionDetailConnect}
            getBillingConnect={getBillingConnect}
            guestUserChangesConnect={guestUserChangesConnect}
            isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
            userId={userGUID}
            userEmail={userData?.email}
            buttonText={t("dashboard.invite_friend_cta")}
            selectedPlan={selectedPlan}
            assessmentData={currentAssessmentAttemptData}
            showRemainingInvites={true}
            bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
          />
        )}
        <ReportWrapper
          assessmentId={assessmentId}
          assessmentAttemptId={assessmentAttemptId}
          currentLocale={currentLocale}
        />
      </div>
      <Footer />
    </>
  );
}

export default BigFiveReport;
